import { Currency, CurrencyAmount, Percent, Token, TradeType } from '@uniswap/sdk-core'
import { FeeAmount, Pool, Route as V3Route } from '@uniswap/v3-sdk'
import { BIPS_BASE } from 'constants/misc'
import { WRAPPED_NATIVE_CURRENCY, nativeOnChain } from 'constants/tokens'
import { getApproveInfo } from 'state/routing/gas'
import {
  ClassicTrade,
  GetQuoteArgs,
  QuoteMethod,
  QuoteState,
  SwapFeeInfo,
  SwapRouterNativeAssets,
  TradeResult,
} from 'state/routing/types'
import type { RouterExecutionData, RouterQuoteResponse } from 'utils/robinhoodRouter/types'

// The native sentinel the router uses for the chain's gas token.
const NATIVE_SENTINEL = '0x0000000000000000000000000000000000000000'

/**
 * Resolves one side of `GetQuoteArgs`' flat token fields (`tokenInAddress` /
 * `tokenInChainId` / `tokenInDecimals` / `tokenInSymbol`, or the `tokenOut*`
 * equivalents) into a `Currency`, mirroring how `state/routing/utils.ts`
 * (`getTradeCurrencies`) derives `currencyIn`/`currencyOut`: the native
 * sentinel becomes `nativeOnChain(chainId)`, any other address becomes the
 * ERC-20 `Token`.
 */
function resolveCurrency(address: string, chainId: number, decimals: number, symbol?: string): Currency {
  const isNative =
    address.toLowerCase() === NATIVE_SENTINEL ||
    Object.values(SwapRouterNativeAssets).includes(address as SwapRouterNativeAssets)
  return isNative ? nativeOnChain(chainId) : new Token(chainId, address, decimals, symbol)
}

/**
 * Builds the router trade's displayed fee, mirroring `getKyberSwapFee` in
 * `state/routing/utils.ts`. The router fee is always taken from the OUTPUT
 * side, so `feeCurrency` is `'output'` and the amount is computed from the
 * GROSS output with the same integer floor the router uses:
 * `feeAmount = grossAmountOut * feeBps / 10000`.
 *
 * For an ordinary (non-native) output, the fee token IS the output token,
 * so the default `feeCurrency: 'output'` resolution is already correct and
 * `feeToken` is left unset. For a native OUTPUT, M3 takes the fee via
 * `PAY_PORTION` in WETH *before* unwrapping the remainder to native ETH —
 * `quote.fee.feeToken` reports that real WETH token, not the native
 * sentinel the trade otherwise displays as its output currency. `feeToken`
 * is set from the chain's known wrapped-native token in that case so the
 * fee row displays WETH instead of a fictional ETH amount (F6, M4 audit).
 */
function getRobinhoodRouterFee(quote: RouterQuoteResponse, currencyOut: Currency): SwapFeeInfo | undefined {
  const feeBps = BigInt(quote.fee.feeBps)
  if (feeBps <= 0n) {
    return undefined
  }
  const gross = BigInt(quote.grossAmountOut)
  const feeAmount = (gross * feeBps) / 10_000n

  let feeToken: Currency | undefined
  if (currencyOut.isNative) {
    const wrappedNative = WRAPPED_NATIVE_CURRENCY[quote.chainId]
    if (wrappedNative && quote.fee.feeToken.toLowerCase() === wrappedNative.address.toLowerCase()) {
      feeToken = wrappedNative
    }
  }

  return {
    recipient: quote.fee.feeRecipient ?? '',
    percent: new Percent(Number(feeBps), BIPS_BASE),
    amount: feeAmount.toString(),
    feeCurrency: 'output',
    ...(feeToken ? { feeToken } : {}),
  }
}

/**
 * Converts a Robinhood router quote into a `ClassicTrade`, constructed the same
 * way `getClassicTradeDetails`/`transformQuoteToTrade` build the Kyber classic
 * trade in `state/routing/utils.ts`: a single synthetic V3 route for display,
 * `inputAmount`/`outputAmount` as `CurrencyAmount`s (output = NET, since that is
 * what the user receives), a `swapFee` derived from the router fee, and the
 * `robinhoodRouterData` execution subset attached for the later build call.
 *
 * The router is exact-input only; exact-output requests return NOT_FOUND.
 */
export async function transformRouterQuoteToTrade(
  args: GetQuoteArgs,
  quote: RouterQuoteResponse,
): Promise<TradeResult> {
  if (args.tradeType !== TradeType.EXACT_INPUT) {
    return { state: QuoteState.NOT_FOUND }
  }

  // GetQuoteArgs carries flat token* fields (no nested tokenIn/tokenOut
  // objects). Mirror utils.ts's currencyIn/currencyOut derivation from those.
  const currencyIn = resolveCurrency(args.tokenInAddress, args.tokenInChainId, args.tokenInDecimals, args.tokenInSymbol)
  const currencyOut = resolveCurrency(
    args.tokenOutAddress,
    args.tokenOutChainId,
    args.tokenOutDecimals,
    args.tokenOutSymbol,
  )

  // Mirror the Kyber path's synthetic single-hop V3 pool/route (utils.ts) so the
  // ClassicTrade has a valid route array for display without depending on live
  // pool state.
  const wrappedIn = (currencyIn.isNative ? currencyIn.wrapped : currencyIn) as Token
  const wrappedOut = (currencyOut.isNative ? currencyOut.wrapped : currencyOut) as Token
  const syntheticPool = new Pool(
    wrappedIn,
    wrappedOut,
    FeeAmount.MEDIUM,
    '79228162514264337593543950336',
    '1000000000000000000',
    0,
  )
  const v3Route = new V3Route([syntheticPool], currencyIn, currencyOut)

  const inputAmount = CurrencyAmount.fromRawAmount(currencyIn, args.amount)
  // NET output: the user receives net, not gross.
  const outputAmount = CurrencyAmount.fromRawAmount(currencyOut, quote.netAmountOut)

  const swapFee = getRobinhoodRouterFee(quote, currencyOut)

  const robinhoodRouterData: RouterExecutionData = {
    routeId: quote.routeId,
    routeEnvelope: quote.routeEnvelope,
    chainId: quote.chainId,
    tokenIn: quote.tokenIn,
    tokenOut: quote.tokenOut,
    amountIn: quote.amountIn,
    netAmountOut: quote.netAmountOut,
    fee: quote.fee,
    expiresAt: quote.expiresAt,
    deploymentVersion: quote.deploymentVersion,
  }

  const approveInfo = await getApproveInfo(args.account, currencyIn, quote.amountIn, undefined)

  const trade = new ClassicTrade({
    v2Routes: [],
    v3Routes: [{ routev3: v3Route, inputAmount, outputAmount }],
    mixedRoutes: [],
    tradeType: TradeType.EXACT_INPUT,
    gasUseEstimate: quote.quoterGasEstimate ? parseFloat(quote.quoterGasEstimate) : undefined,
    blockNumber: quote.blockNumber,
    approveInfo,
    quoteMethod: QuoteMethod.ROUTING_API,
    swapFee,
    robinhoodRouterData,
  })

  return { state: QuoteState.SUCCESS, trade }
}
